import React, { useContext, useState, useEffect } from "react";

const DataContext = React.createContext();

export function useDataContext() {
  return useContext(DataContext);
}

// Sample initial data structure
const initialProblemStatement = {
  problemStatement: [
    {
      category: "software",
      domain: "automation",
      psNumber: "78678",
      psTitle: "yuegfuwgfywfbwfuvewhfbhuebfhuveguvuvfhuevfuh",
      domainBucket: "AI",
    },
  ],
};

export function UserProvider({ children }) {
  const [org, setOrg] = useState([]);
  const [users, setUsers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorCategories, setSponsorCategories] = useState([]);

  // Simulate getting all users from local storage
  async function getAllUsers() {
    try {
      const storedUsers = localStorage.getItem('users');
      if (storedUsers) {
        setUsers(JSON.parse(storedUsers));
      }
    } catch (error) {
      console.log(error);
    }
  }

  // Get problem statements from local storage
  async function getProblemStatements() {
    try {
      const storedOrgs = localStorage.getItem('organisations');
      if (storedOrgs) {
        setOrg(JSON.parse(storedOrgs));
      }
    } catch (error) {
      console.log(error);
    }
  }

  // Add new organization
  async function addOrganisation(orgData) {
    try {
      const newOrg = {
        ...orgData,
        id: Math.random().toString(36).substr(2, 9),
      };
      setOrg(prev => [...prev, newOrg]);

      // Update local storage
      const updatedOrgs = [...org, newOrg];
      localStorage.setItem('organisations', JSON.stringify(updatedOrgs));
    } catch (err) {
      console.log(err);
    }
  }

  // Add problem statement to an organization
  async function addProblemStatement(id, data) {
    try {
      const updatedOrg = org.map(organization => {
        if (organization.id === id) {
          return {
            ...organization,
            problemStatements: [
              ...(organization.problemStatements || []),
              data
            ]
          };
        }
        return organization;
      });

      setOrg(updatedOrg);
      localStorage.setItem('organisations', JSON.stringify(updatedOrg));
    } catch (error) {
      console.log(error);
    }
  }

  // Get sponsor categories
  async function getSponsorCategory() {
    try {
      const categories = sponsors.map(sponsor => sponsor.category);
      return [...new Set(categories)]; // Return unique categories
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  // Get all sponsors
  async function getSponsors() {
    try {
      return sponsors;
    } catch (err) {
      console.log("failed to fetch sponsor data");
      return [];
    }
  }

  // Set new sponsor with image
  async function setImageAndCategory(imageFile, sponsorData, orderNo) {
    const { category, name, website } = sponsorData;
    const modifiedCategory = category.toLowerCase().replace(/\s+/g, "-");

    try {
      // Convert image file to base64 for storage
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageUrl = e.target.result;
        const newEntry = { name, url: imageUrl, website };

        const updatedSponsors = [...sponsors];
        const categoryIndex = updatedSponsors.findIndex(
          s => s.category.toLowerCase().replace(/\s+/g, "-") === modifiedCategory
        );

        if (categoryIndex !== -1) {
          // Category exists
          const sponsorImages = updatedSponsors[categoryIndex].images;
          if (orderNo === "Last") {
            sponsorImages.push(newEntry);
          } else if (orderNo === "First") {
            sponsorImages.unshift(newEntry);
          } else if (typeof orderNo === "number") {
            sponsorImages.splice(orderNo - 1, 0, newEntry);
          }
        } else {
          // New category
          updatedSponsors.push({
            category: category,
            images: [newEntry]
          });
        }

        setSponsors(updatedSponsors);
        localStorage.setItem('sponsors', JSON.stringify(updatedSponsors));
      };

      reader.readAsDataURL(imageFile);
    } catch (err) {
      console.log(err);
    }
  }

  // Initialize data from localStorage on component mount
  useEffect(() => {
    getAllUsers();
    getProblemStatements();

    // Load sponsors from localStorage
    const storedSponsors = localStorage.getItem('sponsors');
    if (storedSponsors) {
      setSponsors(JSON.parse(storedSponsors));
    }
  }, []);

  const value = {
    org,
    users,
    addOrganisation,
    addProblemStatement,
    getSponsors,
    setImageAndCategory,
    getSponsorCategory,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}